/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CRUDPermissions, Devices} from '@ion/core/src/types/entities';
import {ResponsiveMenu} from '@ion/dls/src/components/menus/ResponsiveMenu';
import {CellMenuItem} from '@ion/dls/src/grid/CellMenuItem';
import {CreateJobIcon} from '@ion/dls/src/icons/CreateJobIcon';
import {DropdownArrowIcon} from '@ion/dls/src/icons/DropdownArrowIcon';
import {MoveEntityIcon} from '@ion/dls/src/icons/MoveEntityIcon';
import {PasswordIcon} from '@ion/dls/src/icons/PasswordIcon';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {showJobCreateEditor} from '../../components/jobs/state';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {getESNs} from './DevicePasswordUtils';
import {enableManageServices} from './DeviceServicesUtils';
import {checkSCIDevice} from './SCISettingsUtils';
import {
  moveAdminDevices,
  showManageServicesDialog,
  viewBulkEditGroup,
  viewDevicePassword
} from './state';

// defines the selections header styles ...
export const useStyles = makeStyles(theme => ({
  selectionsLabel : {
    ...dlsTypography('selectionsLabel')
  },
  menuPaper : {
    marginTop : '49px'
  }
}));

// defines the device action menu prop definitions ...
export interface DevicesActionMenuTypes {
  selectedDevices: Devices;
  onSCIUpdateClick?: Function;
  areTotalRowsSelected?: boolean
  devicesPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
}

/**
 * Defines the DevicesActionMenu component. This component contains
 * the row actions for Devices grid.
 */
export const DevicesActionMenu = ({
  selectedDevices,
  onSCIUpdateClick,
  areTotalRowsSelected,
  devicesPermissions,
  hasCreateJob
}: DevicesActionMenuTypes) => {
  const dispatch                              = useDispatch();
  const color                                 = dlsColor('white');
  const classes                               = useStyles();
  const serviceMenuRef                        = useRef(null);
  const bulkMenuRef                           = useRef(null);
  const [bulkMenuOpen, setBulkMenuOpen]       = useState<boolean>(false);
  const [serviceMenuOpen, setServiceMenuOpen] = useState<boolean>(false);
  const loginUserApplications                 = useAuthenticatedUser().applications;
  const {
    hasAddPermission,
    hasUpdatePermission,
    hasReadPermission,
    hasDeletePermission
  } = devicesPermissions || {};

  const onDevicePassword = useCallback(() => {
    dispatch(viewDevicePassword(getESNs(selectedDevices), 'esn'));
  }, [selectedDevices]);

  const onMove = useCallback(() => {
    dispatch(moveAdminDevices(selectedDevices));
  }, [selectedDevices]);

  const closeBulkMenu = useCallback(() => {
    setBulkMenuOpen(false);
  }, [bulkMenuOpen]);

  const onToggleBulkMenu = useCallback(() => {
    setBulkMenuOpen(!bulkMenuOpen);
  }, [bulkMenuOpen]);

  const onToggleJobMenu = useCallback(() => {
    dispatch(showJobCreateEditor(selectedDevices, null, true));
  }, [selectedDevices]);

  const onBulkEditGroup = useCallback(() => {
    dispatch(viewBulkEditGroup(selectedDevices));
    closeBulkMenu();
  }, [selectedDevices]);

  const closeServiceMenu = useCallback(() => {
    setServiceMenuOpen(false);
  }, [serviceMenuOpen]);

  const onToggleServicesMenu = useCallback(() => {
    setServiceMenuOpen(!serviceMenuOpen);
  }, [serviceMenuOpen]);

  const onManageServices = useCallback(() => {
    dispatch(showManageServicesDialog(selectedDevices));
    closeServiceMenu();
  }, [selectedDevices]);

  const onBulkSCIUpdate = useCallback(() => {
    onSCIUpdateClick();
    closeBulkMenu();
  }, [dispatch, selectedDevices, areTotalRowsSelected]);

  const sciDisabled = useMemo(() => (
    areTotalRowsSelected ? !areTotalRowsSelected : !selectedDevices?.some(checkSCIDevice)
  ), [selectedDevices, areTotalRowsSelected]);

  return (
    <>
      <div ref={serviceMenuRef}>
        <CellMenuItem customStyles={{root: classes.selectionsLabel}}
          icon={<DropdownArrowIcon color={color} />}
          onClick={onToggleServicesMenu}
          label={translate('admin-devices:menu.services')} />
      </div>
      <ResponsiveMenu open={serviceMenuOpen}
        onClose={onToggleServicesMenu}
        anchorEl={serviceMenuRef.current}
        menuClasses={{paper: classes.menuPaper}}>
        <CellMenuItem onClick={onManageServices}
          disabled={!enableManageServices(loginUserApplications)}
          label={translate('admin-devices:menu.manageServices')} />
      </ResponsiveMenu>
      <div ref={bulkMenuRef}>
        <CellMenuItem customStyles={{root: classes.selectionsLabel}}
          icon={<DropdownArrowIcon color={color} />}
          onClick={onToggleBulkMenu}
          label={translate('admin-devices:menu.bulk')} />
      </div>
      <ResponsiveMenu open={bulkMenuOpen}
        onClose={onToggleBulkMenu}
        anchorEl={bulkMenuRef.current}
        menuClasses={{paper: classes.menuPaper}}>
        <CellMenuItem onClick={onBulkEditGroup}
          label={translate('admin-devices:menu.editGroup')} />
        <CellMenuItem onClick={onBulkSCIUpdate}
          disabled={sciDisabled}
          label={translate('admin-devices:view.bulkSciSetting.sciUpdate')} />
      </ResponsiveMenu>
      <CellMenuItem customStyles={{root: classes.selectionsLabel}}
        icon={<MoveEntityIcon color={color} />}
        onClick={onMove}
        disabled={!hasUpdatePermission || areTotalRowsSelected}
        label={translate('admin-devices:menu.move')} />
      <CellMenuItem customStyles={{root: classes.selectionsLabel}}
        icon={<PasswordIcon color={color} />}
        onClick={onDevicePassword}
        disabled={!hasUpdatePermission || areTotalRowsSelected}
        label={translate('admin-devices:menu.devicePassword')} />
      <CellMenuItem customStyles={{root: classes.selectionsLabel}}
        icon={<CreateJobIcon color={color} />}
        onClick={onToggleJobMenu}
        disabled={!hasCreateJob}
        label={translate('admin-devices:menu.createJob')} />
    </>
  );
};
export default DevicesActionMenu;
